import { LinkedinCards } from "domain/entities/linkedincards";
import { LinkedinCardsInputFilters } from "domain/models/linkedincards-input-filters";
import Spinner from "presentation/components/Shared/Spinner";
import { useLinkedInCardsByFilter } from "presentation/hooks/use-query-linkedincards";
import { useFilterLinkedinCardsStore } from "presentation/stores/use-filter-linkedincards-store";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import LinkedinCardsForm from "../LinkedinCardsForm";
import HeaderLinkedinCardsGrid from "./HeaderLinkedinCardsGrid";
import TypeLinkedinCardsDropdown from "./TypeLinkedinCardsDropdown";
import { useDeleteLinkedinCardsMutation } from "presentation/hooks/use-query-linkedincards";
import { useLinkedinCardsSelectedStore } from "presentation/stores/use-linkedincards-selected-store";
import { Calendar } from "primereact/calendar";
import { Tooltip } from "primereact/tooltip";
import { formatDateSafe } from "presentation/utils/date-formatter";


const LinkedinCardsGrid: React.FC = () => {
  const { filters, setFilters } = useFilterLinkedinCardsStore();
  const toast = useRef<Toast>(null);
  const deleteMutation = useDeleteLinkedinCardsMutation();
  const { selectedCard, setSelectedCard } = useLinkedinCardsSelectedStore();
   const [showModal, setShowModal] = useState(false);
  const [lazyParams, setLazyParams] = useState({
    sortBy: "createdAt",
    sortOrder: "desc",
    limit: 50,
    page: 1,
    filters: {}
  });
  const {
    data: linkedinCards,
    isLoading,
    isError,
    error,
  } = useLinkedInCardsByFilter({
          sortBy: lazyParams.sortBy,
          sortOrder: lazyParams.sortOrder,
          page: lazyParams.page,
          limit: lazyParams.limit,
          ...lazyParams.filters,
        } as LinkedinCardsInputFilters);

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      page: 1,
      filters: Object.fromEntries(
        Object.entries(filters).map(([key, value]) => {
          if ("constraints" in value) {
            return [key, value.constraints?.[0]?.value ?? ""];
          }
          return [key, (value as DataTableFilterMetaData)?.value ?? ""];
          }).filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== ""
            )),}));
        }, [filters]);
  if (isLoading) return <Spinner />;

  if (isError && toast.current) {
    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail: error?.message || "Ocorreu um erro inesperado",
      life: 1500,
    });
  }

  
const handleDelete = async(id: string) => {
    try {
      if (!id) throw new Error("ID inválido");
      await deleteMutation.mutateAsync(id);
    } catch (error: any) {
      showToastError("Erro ao apagar posição.");
    }
  }

  const confirmAction = (message: string, acceptCallback: () => void) => {
        confirmDialog({
          message: message,
          header: "Confirmação",
          icon: "pi pi-exclamation-triangle",
          acceptLabel: "Sim",
          rejectLabel: "Não",
          accept: acceptCallback,
        });
      };

  const showActionOptions = (rowData: LinkedinCards) => {
    return (
      <div className="spacing-icons">
        <i
          className="pi pi-times cursor-pointer"
          data-pr-tooltip="Apagar posição"
          onClick={() => {
            confirmAction(`Deseja apagar esta posição?`, () =>
              handleDelete(rowData.id));
          }}
        />
        <i
          className="pi pi-eye cursor-pointer"
          data-pr-tooltip="Ver detalhes"
          onClick={() => {
            setSelectedCard(rowData);
            setShowModal(true);
          }}
        />
      </div>
    );
  };

  const showToastError = (detail: string) => {
        toast.current?.show({ severity: "error", summary: "Erro", detail, life: 1500 });
    };

  const tooltipStyle = {
    root: { style: { boxShadow: "none", border: "none", transition: "none", animation: "none" } },
    text: { style: { whiteSpace: "nowrap" as const } },
    arrow: { style: { display: "none" } },
  };

  return (
    <div>
      <Tooltip target="[data-pr-tooltip]" position="top" pt={tooltipStyle} />
      <Dialog
        header="Detalhes da Posição"
        visible={showModal}
        onHide={() => setShowModal(false)}
        style={{ width: "80vw", maxHeight: "90vh" }}
        contentStyle={{ overflowY: "auto" }}
      >
        <LinkedinCardsForm
          initialData={selectedCard}
          id={selectedCard?.id}
          onSuccess={() => setShowModal(false)}
        />
      </Dialog>
      <DataTable
        value={linkedinCards?.data || []}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={linkedinCards?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1
                ? "desc"
                : e.sortOrder === 1
                ? "asc"
                : "",
              
          })
        }
        onFilter={(e) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="fit"
        header={<HeaderLinkedinCardsGrid />}
        rowsPerPageOptions={[2, 10, 25, 50]}
        dataKey="id"
        globalFilterFields={[
          "title",
          "companyName",
          "companyLocation"
        ]}
      >
        <Column 
          field="title" 
          header="Perfil" 
          sortable 
          filter 
          style={{
          maxWidth: "200px",
          width: "200px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
          showFilterMatchModes={false}
        />
        <Column 
          field="companyName" 
          header="Empresa" 
          sortable 
          filter 
          style={{
          maxWidth: "200px",
          width: "200px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
          showFilterMatchModes={false}
        />
        <Column 
          field="companyLocation" 
          header="Localização" 
          sortable 
          filter 
          style={{ width: "1%" }}
          showFilterMatchModes={false}
        />
        <Column 
          field="jobType" 
          header="Tipo de Emprego" 
          sortable 
          filter 
          showFilterMatchModes={false}
          filterElement={(options) => (
            <TypeLinkedinCardsDropdown options={options} />
          )}
          style={{ width: "1%" }}
        />
        <Column 
          field="listedDate"
          body={(rowData) => formatDateSafe(rowData.listedDate)}
          header="Data de Publicação" 
          sortable 
          filter 
          filterElement={(options) => (
            <Calendar
                value={options.value ? new Date(options.value) : null}
                onChange={(e) =>
                  options.filterApplyCallback(
                    e.value ? e.value.toISOString() : null
                  )}
                dateFormat="dd-mm-yy"
                showIcon
                className="w-full"
                placeholder="Selecionar data"
                showButtonBar
              />)}
          showFilterMatchModes={false}
          style={{ width: "1%" }}
        />
        <Column
          body={showActionOptions}
          header="Ações"
          align="center"
          alignHeader="center"
          style={{ width: "1%" }}
        />
      </DataTable>

      <Toast ref={toast} position="bottom-right" />
      <ConfirmDialog />
    </div>
  );
};

export default LinkedinCardsGrid;